import React, { useState } from 'react';
import styled from 'styled-components';

import { Logo } from '../../../ui/logo/Fritid/Index';
import { Container } from '../../../layout/Container';
import { Section } from '../../../layout/Section';
import { Seo } from '../../../../utils/seo/Index';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Button } from '../../../ui/button/Button';
import FritidKontor from './FritidKontor';

import { ButtonLink } from '../../../ui/link/ButtonLink';
import { Heading } from '../../../ui/text/Heading';
import { Thanks } from '../../../layout/Thanks';
import { animated } from 'react-spring';

import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { createLeadMutation } from '../../../../../client/__graphql__/mutations';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from '../../../../utils/hooks/useForm';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Form, FormRow, FormWrapper } from '../../../ui/form/Form';
import { FormError } from '../../../ui/form/FormError';
import { Input } from '../../../ui/form/Input';
import { useScrollToElement } from '../../../../utils/hooks/useScrollToElement';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';
import { Checkbox } from '../../../ui/form/Checkbox';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import ButtonGroupGrid from '../../../ui/button/ButtonGroupGrid';
import Sok from './Sok';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { LeisureSearchContextProvider } from '../context';
import LeisureBrochure from './FritidsBrosjyre';
import { IAreaItem, AreaRowItem } from './AreaRowItem';

//GraphQL
import { GET_PAGE_OFFICES } from '../../../../../client/__graphql__/queries/page/getPageOffices';
import {
  GetPageMapAreasQuery,
  GetPageMapAreasQueryVariables,
  GetPageOfficesQuery,
  GetPageOfficesQueryVariables,
  Office,
  PageAreas
} from '../../../../__types__/generated';
import { GET_PAGE_MAP_AREAS } from '../../../../../client/__graphql__/queries/page/getPageMapAreas';

const urltag = 'fritid';

import { CarouselButtonRight } from '../../../ui/button/ButtonCarouselRight';
import { CarouselButtonLeft } from '../../../ui/button/ButtonCarouselLeft';
import SocialMedia from './SocialMedia';
import {
  Image,
  ImageCarouselBulletsWithNoAnimation
} from '../../../ui/carousel/ImageCarouselBulletsWithNoAnimation';
import SearchProperties from '../search/Index';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const Index: React.FC = () => {
  const formRef = React.useRef(null);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const { routeConfig } = React.useContext(SiteContext);
  const [sale, setSale] = useState(true);
  const [loading, setLoading] = React.useState(false);
  const { submitWithDigger } = useDigtectiveContext();
  const [areas, setAreas] = useState<IAreaItem[]>([]);

  const { scrollTo } = useScrollToElement();
  const [posted, setPosted] = React.useState(false);
  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
        success
      }`
    }
  });

  const VALUATION_MUTATION = createLeadMutation({
    valuation: {
      response: `{
        success
        data {
          id
          address
          zip
          phone
          email
        }
      }`
    }
  });

  const { data: offices } = useQuery<
    GetPageOfficesQuery,
    GetPageOfficesQueryVariables
  >(GET_PAGE_OFFICES, {
    variables: { input: { value: urltag } }
  });

  const { data: areasData } = useQuery<
    GetPageMapAreasQuery,
    GetPageMapAreasQueryVariables
  >(GET_PAGE_MAP_AREAS, {
    variables: { input: { value: urltag } },
    onCompleted: ({ getPageMapAreas }) => {
      if (getPageMapAreas?.count > 0 && getPageMapAreas?.list) {
        const array = getPageMapAreas.list.map<IAreaItem>(
          (e: PageAreas, index: number) => {
            return {
              id: e?.id ? e?.id : 0,
              name: e?.name ? e.name : '',
              selected: false
            };
          }
        );

        setAreas(array);
      }
    }
  });

  const [mail, { loading: mailLoading, error }] = useMutation<
    ILeadResponse,
    ILeadInput
  >(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead && lead.mail && lead.mail.success) {
        setPosted(true);
      }
    }
  });

  const [lead, { error: leadError }] = useMutation<ILeadResponse, ILeadInput>(
    VALUATION_MUTATION,
    {
      onCompleted: ({ lead }) => {
        setLoading(false);
        if (lead.valuation?.success) {
          window.scroll(0, 0);
          setPosted(true);

          // GTM tracking

          createGTMEvent({
            event: 'pmFormSubmission',
            gaEvent: 'Verdivurdering NY',
            gaCategory: 'Form',
            gaAction: 'Submit'
          });
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Ola Nordmann',
        value: '',
        label: 'Navn'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: '1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: '00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields = getFields(fields);
      setLoading(true);

      if (sale) {
        submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
          lead({
            variables: {
              input: {
                ...preparedFields,
                diggerId,
                referer: `${location.pathname}${location.search}`,
                leadType: 'VERDIVURDERING',
                pageType: 'GENERELL'
              }
            }
          });
        });
      } else {
        mail({
          variables: {
            input: {
              ...preparedFields,
              referer: `${location.pathname}${location.search}`,
              leadType: 'EPOST',
              mailType: 42
            }
          }
        });
      }
    }
  });

  return (
    <>
      <Seo
        title="PrivatMegleren Fritid"
        description="Vi tilpasser salgsprosessen og skreddersyr salget til nettopp din fritidsbolig. Kontakt et av våre fritidskontor for e-takst eller verdivurdering."
        image="https://cdn.reeltime.no/pm_assets/fritid/bakgrunn_fritid_old.jpg"
        imageTitle="/verdivurdering-hytte-fritidseiendom-salg-av-eiendom"
        url={'https://privatmegleren.no/fritid'}
      />

      <FritidStyle>
        <Container style={{ paddingTop: '100px' }}>
          <Wrapper style={fadeOut}>
            <Section className="center">
              <Logo alt="bilde av logo" />
              <Paragraph center={true}>
                PrivatMegleren er godt etablert både til fjells og ved sjøen.
                Med landets mest drevne og engasjerte fritidsmeglere, har vi
                full oversikt over hva som rører seg i markedet for
                fritidseiendom til enhver tid.
              </Paragraph>
              <Paragraph center={true}>
                PrivatMegleren er den eneste megleraktøren som tilbyr bistand
                til kjøp av eiendom satt i system. Bistand av megler til kjøp av
                eiendom er fort tidsbesparende, mindre risikabelt og lønnsomt.
              </Paragraph>
              <Paragraph style={{ paddingBottom: '30px' }} center={true}>
                Vi har god dialog med hytteeiere i områdene vi opererer i. Det
                er en av grunnene til at vi kjenner fritidsmarkedet ekstra godt.
                Vår rolle som rådgiver er derfor svært verdifull for kundene
                våre som selgere og kjøpere av fritidseiendom.
              </Paragraph>
              <Container>
                <ButtonGroupGrid className="col-4">
                  <Button
                    className="blikontaktet"
                    type="primary"
                    colorTheme="gold"
                    fullWidth={true}
                    center={true}
                    onClick={() => {
                      if (typeof scrollTo === 'function') {
                        scrollTo({
                          ref: formRef,
                          distanceTop: 100,
                          behaviour: 'smooth'
                        });
                      }
                    }}
                  >
                    Bli kontaktet
                  </Button>
                  <Button
                    className="fritidseiendommer"
                    type="secondary"
                    colorTheme="gray"
                    fullWidth={true}
                    center={true}
                    onClick={() => {
                      if (typeof scrollTo === 'function') {
                        scrollTo({
                          ref: searchRef,
                          distanceTop: 100,
                          behaviour: 'smooth'
                        });
                      }
                    }}
                  >
                    Våre fritidseiendommer
                  </Button>
                  <Button
                    as="link"
                    className="fritidseiendommer"
                    type="secondary"
                    colorTheme="gray"
                    fullWidth={true}
                    center={true}
                    to="/leveransebeskrivelse-selge-bolig"
                  >
                    Vår leveranse
                  </Button>
                </ButtonGroupGrid>
              </Container>
              <SocialMedia />
              <ImageCarousel />
              {!(
                offices?.getPageOffices &&
                offices?.getPageOffices?.count > 0 &&
                offices?.getPageOffices?.list
              ) ? null : (
                <>
                  <Heading
                    tag="h2"
                    style={{ marginTop: '1.5em' }}
                    center={true}
                  >
                    Fritidskontorer
                  </Heading>
                  <OfficeGrid>
                    {offices?.getPageOffices?.list?.map(
                      (office: Office, index: number) => {
                        if (office?.name) {
                          return (
                            <FritidKontor
                              key={`office${index}`}
                              adresse={
                                office?.address ? office?.address : undefined
                              }
                              phoneNumber={
                                office?.phone ? office?.phone : undefined
                              }
                              officeName={office?.name}
                              urltag={
                                office?.urltag ? office?.urltag : undefined
                              }
                            />
                          );
                        }
                      }
                    )}
                  </OfficeGrid>
                </>
              )}
              {!(areas && areas?.length) ? null : (
                <BrokersWrapper>
                  <Heading tag="h2" center={true}>
                    Fritidsmeglere
                  </Heading>
                  <Paragraph center={true} className="form-text">
                    Trykk på et område og finn din fritidsmegler
                  </Paragraph>
                  {areas?.map((obj: IAreaItem, index: number) => {
                    return (
                      <AreaRowItem
                        key={`area${index}`}
                        area={obj}
                        urlTag={urltag}
                      />
                    );
                  })}
                </BrokersWrapper>
              )}
            </Section>
            <SearchProperties ref={searchRef} />
            {/*}
            <LeisureSearchContextProvider>
              <Sok ref={searchRef} />
                </LeisureSearchContextProvider>*/}
            <FormWrapper ref={formRef} style={fadeOut}>
              <Section
                className="form-section"
                style={{ paddingBottom: '10px' }}
              >
                <Heading tag="h2" center={true}>
                  Bli kontaktet
                </Heading>
                <Paragraph center={true} className="form-text">
                  Vi har god dialog med hytteeiere i områdene vi opererer i. Det
                  er en av grunnene til at vi kjenner fritidsmarkedet ekstra
                  godt. Vår rolle som rådgiver er derfor svært verdifull for
                  kundene våre som
                  <br />
                  selgere og kjøpere av fritidseiendom.
                </Paragraph>

                <CheckboxGroup style={{ padding: '2em 2em' }}>
                  <Checkbox
                    checked={sale}
                    labelPos="right"
                    label="Selge"
                    onClick={() => setSale((prev) => !prev)}
                  />
                  <Checkbox
                    checked={!sale}
                    labelPos="right"
                    label="Kjøp"
                    onClick={() => setSale((prev) => !prev)}
                  />
                </CheckboxGroup>
              </Section>
              <Form
                onSubmit={handleSubmit}
                noValidate
                style={{ paddingBottom: '50px' }}
              >
                {error &&
                  error.graphQLErrors.map((error, index) => (
                    <FormError key={`error_${index}`} message={error.message} />
                  ))}
                {fields.map((item) => {
                  if (item) {
                    return (
                      <FormRow key={`field_${item.name}`}>
                        <Input
                          type={item.type}
                          name={item.name}
                          label={item.label}
                          labelAfter={item.labelAfter}
                          placeholder={item.placeholder}
                          value={item.value}
                          onChange={handleChange}
                          error={item.error}
                        />
                      </FormRow>
                    );
                  }
                })}
                <ButtonGroup>
                  <Button
                    type="primary"
                    colorTheme="gold"
                    disabled={loading}
                    loading={loading}
                  >
                    Kontakt meg
                  </Button>
                  <ButtonLink
                    as="a"
                    href={routeConfig?.personvern.url}
                    className="privacy-link-mobile"
                  >
                    Personvernpolicy
                  </ButtonLink>
                </ButtonGroup>
              </Form>
            </FormWrapper>
          </Wrapper>

          <Thanks style={fadeIn}>
            {sale ? (
              <>
                <Section className="form-section">
                  <Heading tag="h2" center={true}>
                    Ta kontakt for kjøp av fritidseiendom
                  </Heading>
                  <AccordionGeneral />
                </Section>
                <Section className="form-section" style={{ paddingTop: 0 }}>
                  <AccordionBenefits />
                </Section>
              </>
            ) : (
              <Section className="form-section">
                <Heading tag="h2" center={true}>
                  Tusen takk
                </Heading>
                <Paragraph center={true}>
                  En eiendomsmegler vil ta kontakt med deg så snart som mulig.
                </Paragraph>
              </Section>
            )}
          </Thanks>
        </Container>

        <BackgroundLoad
          opacity={0.1}
          height={'70vh'}
          position="top center"
          url="https://cdn.reeltime.no/pm_assets/fritid/bakgrunn_fritid_old.jpg"
          showOnMobile={true}
        />
      </FritidStyle>
    </>
  );
};

const ImageCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 < 0 ? array.length - 1 : prev - 1));
  };
  const next = () => {
    setCurrentIndex((prev) => (prev + 1 > array.length - 1 ? 0 : prev + 1));
  };

  const select = (index: number) => {
    setCurrentIndex(index);
  };
  const array = [
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/1.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/2.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/3.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/4.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/5.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/6.jpg'
    },
    {
      url: 'https://cdn.reeltime.no/pm_assets/fritid/img/7.jpg'
    }
  ];
  return (
    <>
      <StyledCarouselWrapper>
        <CarouselSection>
          <Container>
            <CarouselImageWrapper>
              <CarouselButtonRight className="right" onClick={prev} />

              {array.map((item, index) => {
                if (index === currentIndex) {
                  return (
                    <ImageWrapper key={index}>
                      <Image className="img" key={index} url={item.url} />
                    </ImageWrapper>
                  );
                } else {
                  return null;
                }
              })}
              <ImageCarouselBulletsWithNoAnimation
                className="bullets"
                images={array}
                select={select}
                currentIndex={currentIndex}
              />
              <CarouselButtonLeft className="left" onClick={next} />
            </CarouselImageWrapper>
          </Container>
        </CarouselSection>
      </StyledCarouselWrapper>
    </>
  );
};

const FritidStyle = styled.div`
  .fritidseiendommer {
    justify-self: center;
  }

  .blikontaktet {
    justify-self: center;
  }
`;
const ImageWrapper = styled.div`
  img {
    height: 39.9375em;
  }
`;

const Wrapper = styled(animated.div)``;

export const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, max-content);
  grid-auto-flow: column;
  column-gap: 1em;
  justify-content: center;
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 180px);
  grid-column-gap: 4em;
  margin-top: 3em;

  @media all and (min-width: 800px) {
    grid-template-columns: repeat(2, minmax(120px, 180px));
  }
  @media all and (min-width: 1000px) {
    grid-template-columns: repeat(3, minmax(120px, 180px));
  }
  @media all and (min-width: 1200px) {
    grid-template-columns: repeat(4, minmax(120px, 180px));
  }
`;

const BrokersWrapper = styled.div`
  padding-top: 4em;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export default Index;

const StyledCarouselWrapper = styled.div`
  @media all and (max-width: 1000px) {
    .left  {
      padding-right: 4em;
    }
    .right {
      padding-left: 4em;
    }
  }

  .left  {
    right: -70px;
  }
  .right {
    left: -70px;
  }
  .bullets {
    position: relative;
    width: 100%;
    max-width: 400px;
  }
`;

const CarouselSection = styled(animated.div)`
  display: block;
  text-align: center;
`;

const CarouselImageWrapper = styled.div`
  position: relative;
  display: block;
`;
